import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from '../../context/CartContext';
import { Plus, Minus, ArrowLeft, ShoppingCart, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import { menuItems } from '../../data/menuItems';
import Sidebar from '../sections/Sidebar';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: 'veg' | 'non-veg';
}

interface CartItem extends MenuItem {
  quantity: number;
}

const Menu = () => {
  const [items, setItems] = useState<MenuItem[]>(menuItems);
  const [activeCategory, setActiveCategory] = useState<'all' | 'veg' | 'non-veg'>('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const navigate = useNavigate();
  const { cartItems, total } = useCart();

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('menu_items')
          .select('*')
          .order('name');

        if (error) throw error;

        if (data) {
          setItems(data.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description || '',
            price: Number(item.price),
            image: item.image_url,
            category: item.category as 'veg' | 'non-veg'
          })));
        }
      } catch (error) {
        console.error('Error fetching menu items:', error);
        toast.error('Failed to load menu items. Using offline data.');
        setItems(menuItems);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  const debouncedSearch = useCallback(
    debounce((value: string) => setSearchQuery(value), 300),
    []
  );

  const filteredItems = items
    .filter(item => (activeCategory === 'all' ? true : item.category === activeCategory))
    .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortOption === 'a-z') return a.name.localeCompare(b.name);
      if (sortOption === 'z-a') return b.name.localeCompare(a.name);
      if (sortOption === 'low-high') return a.price - b.price;
      if (sortOption === 'high-low') return b.price - a.price;
      return 0;
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  if (loading) {
    return (
      <>
        <Sidebar />
        <section id="menu" className="py-10 sm:py-20 bg-white dark:bg-gray-800 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button onClick={() => navigate(-1)} className="md:hidden flex items-center text-gray-900 dark:text-white mb-4">
              <ArrowLeft className="w-6 h-6 mr-2" /> Back
            </button>
            <h2 className="font-cormorant text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">Our Menu</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                  <div className="h-24 sm:h-48 bg-gray-300 dark:bg-gray-700" />
                  <div className="p-3 sm:p-4">
                    <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <section id="menu" className="py-10 sm:py-20 bg-white dark:bg-gray-800 pb-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="md:hidden flex items-center font-inter text-gray-900 dark:text-white mb-4">
            <ArrowLeft className="w-6 h-6 mr-2" /> Back
          </button>
          <h2 className="font-cormorant text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">Our Menu</h2>
          <div className="flex flex-col space-y-4 sm:space-y-6 mb-6 sm:mb-8">
            <div className="flex justify-center">
              <input
                type="text"
                placeholder="Search items..."
                onChange={(e) => debouncedSearch(e.target.value)}
                className="w-full sm:w-1/2 px-4 py-2 text-sm sm:text-base rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-center sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="flex justify-center space-x-2 sm:space-x-4">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base rounded-lg font-inter ${
                    activeCategory === 'all'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveCategory('veg')}
                  className={`px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base rounded-lg font-inter ${
                    activeCategory === 'veg'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  Veg
                </button>
                <button
                  onClick={() => setActiveCategory('non-veg')}
                  className={`px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base rounded-lg font-inter ${
                    activeCategory === 'non-veg'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  Non-Veg
                </button>
              </div>
              <div className="flex justify-center">
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="w-full sm:w-auto px-2 py-2 text-sm sm:text-base rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Sort By</option>
                  <option value="a-z">A to Z</option>
                  <option value="z-a">Z to A</option>
                  <option value="low-high">Price: Low to High</option>
                  <option value="high-low">Price: High to Low</option>
                </select>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {currentItems.map(item => (
              <MenuCard key={item.id} item={item} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-8 mb-12 space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => paginate(page)}
                    className={`px-3 py-2 rounded-lg ${
                      currentPage === page
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
        <CartBar
          openModal={() => setIsCartModalOpen(true)}
          totalPrice={total}
          totalItems={totalItems}
          selectedItems={cartItems}
          formData={null}
        />
      </section>
      {isCartModalOpen && <CartModal closeModal={() => setIsCartModalOpen(false)} navigate={navigate} />}
    </>
  );
};

const MenuCard: React.FC<{ item: MenuItem }> = ({ item }) => {
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const [isAdded, setIsAdded] = useState(false);
  const [quantity, setQuantity] = useState(getItemQuantity(item.id));

  useEffect(() => {
    setQuantity(getItemQuantity(item.id));
    setIsAdded(getItemQuantity(item.id) > 0);
  }, [getItemQuantity, item.id]);

  const handleAddToCart = () => {
    addToCart({ ...item });
  };

  const handleIncrement = () => {
    const currentQuantity = getItemQuantity(item.id);
    if (currentQuantity === 0) {
      addToCart({ ...item });
    } else {
      const newQuantity = quantity + 1;
      updateQuantity(item.id, newQuantity);
    }
  };

  const handleDecrement = () => {
    const newQuantity = quantity - 1;
    updateQuantity(item.id, newQuantity);
  };

  const buttonColor = !isAdded
    ? 'bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-600'
    : item.category === 'veg'
    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
    : 'bg-red-600 hover:bg-red-700 text-white';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all hover:scale-105">
      <div className="flex flex-row sm:flex-col items-center sm:items-stretch">
        <img
          src={item.image}
          alt={item.name}
          className="w-16 h-16 sm:w-full sm:h-48 object-cover rounded-md sm:rounded-none flex-shrink-0"
        />
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between sm:justify-start ml-3 sm:ml-0">
          <div className="flex items-center justify-between sm:flex-col sm:items-start sm:mb-2 gap-2">
            <div className="flex items-center gap-2">
              {item.category === 'veg' ? (
                <img src="https://freesvg.org/img/1531813273.png" alt="Veg" className="w-4 h-4 sm:w-6 sm:h-6" />
              ) : (
                <img src="https://freesvg.org/img/1531813245.png" alt="Non-Veg" className="w-4 h-4 sm:w-6 sm:h-6" />
              )}
              <h3 className="font-cormorant text-sm sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                {item.name}
              </h3>
            </div>
            <span className="font-inter text-emerald-600 dark:text-emerald-400 font-semibold text-xs sm:text-base">
              £{item.price.toFixed(2)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-col sm:mt-0 mt-1">
            <p className="font-inter text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-2 sm:mb-4 line-clamp-2">
              {item.description}
            </p>
            <div className="flex items-center justify-between sm:flex-row sm:gap-2">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={handleDecrement}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <span className="font-inter text-gray-900 dark:text-white w-6 sm:w-8 text-center text-xs sm:text-base">
                  {quantity}
                </span>
                <button
                  onClick={handleIncrement}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                className={`font-inter px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-base rounded-lg ${buttonColor} transition-colors`}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CartModal: React.FC<{ closeModal: () => void; navigate: (path: string) => void }> = ({ closeModal, navigate }) => {
  const { cartItems, total, updateQuantity, removeFromCart } = useCart();

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
      navigate('/menu');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md mx-4 p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-cormorant text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Your Cart</h3>
          <button onClick={() => { closeModal(); navigate('/menu'); }} className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        {cartItems.length === 0 ? (
          <p className="font-inter text-gray-600 dark:text-gray-400 text-center">Your cart is empty.</p>
        ) : (
          <>
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-4">
                  <img src={item.image} alt={item.name} className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md" />
                  <div className="flex-1">
                    <h4 className="font-inter text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">
                      {item.name}
                    </h4>
                    <p className="font-inter text-gray-600 dark:text-gray-400 text-xs sm:text-sm">
                      £{item.price.toFixed(2)} x {item.quantity} = £{(item.price * item.quantity).toFixed(2)}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="font-inter text-gray-900 dark:text-white w-6 text-center text-xs sm:text-base">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="font-inter text-red-600 hover:text-red-700 text-xs sm:text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between items-center">
                <span className="font-inter text-gray-900 dark:text-white text-sm sm:text-base font-semibold">
                  Total:
                </span>
                <span className="font-inter text-emerald-600 dark:text-emerald-400 text-sm sm:text-base font-semibold">
                  £{total.toFixed(2)}
                </span>
              </div>
              <button
                onClick={() => {
                  closeModal();
                  navigate('/checkout');
                }}
                className="w-full mt-4 font-inter px-4 py-2 text-sm sm:text-base rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CartBar: React.FC<{
  openModal: () => void;
  totalPrice: number;
  totalItems: number;
  selectedItems: CartItem[];
  formData: any;
}> = ({ openModal, totalPrice, totalItems, selectedItems, formData }) => {
  const navigate = useNavigate();

  if (totalItems === 0) return null;

  return (
    <div
      id="cart-bar"
      className="sticky bottom-4 z-10 w-full px-4 sm:px-6 lg:px-8 bg-[#E6F4F1] dark:bg-[#D1E9E5] shadow-2xl border border-gray-300 dark:border-gray-600 rounded-lg"
    >
      <div className="py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="font-inter text-gray-900 dark:text-gray-800 text-sm sm:text-base">
            £{totalPrice.toFixed(2)}
          </span>
          <span className="font-inter text-gray-600 dark:text-gray-500 text-sm sm:text-base">
            {totalItems} Item{totalItems !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex space-x-2 sm:space-x-4">
          <button
            onClick={openModal}
            className="font-inter px-3 py-2 text-sm sm:text-base rounded-lg bg-white dark:bg-gray-100 text-gray-900 dark:text-gray-800 border border-gray-300 dark:border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-200 transition-colors"
          >
            View Items
          </button>
          <button
            onClick={() => navigate("/checkout", { state: { formData, selectedItems } })}
            className="font-inter px-3 py-2 text-sm sm:text-base rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center"
          >
            <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Proceed to Checkout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Menu;